/**
 * WorkflowConsolePage · Overwatch Auto-System · Focus-Mode
 * ────────────────────────────────────────────────────────
 * The new 3-column workflow console. One appraisal at a time.
 *
 *   ┌ Top: StatusPipelineTabs (all 11 statuses, click to switch)
 *   ├ Header: appraisal identity card
 *   ├────────────┬──────────────────────┬──────────────────────────
 *   │ StatusRail │ TaskTreeMiddle       │ Right pane (runner OR
 *   │ (11 items) │ (tasks + actions of  │  entry-choice cards for
 *   │            │  the active status)  │  Client Data · new/existing)
 *   └────────────┴──────────────────────┴──────────────────────────
 *
 * Right-pane logic:
 *  - Client Data active + no active run  → New Client / Existing Client entry cards
 *  - Any other status active + no run    → "Start run" prompt
 *  - Active run exists                   → embed WorkflowRunnerPage inline
 *  - Locked status active                → PipelineGateBanner + task tree read-only
 *
 * State fetched from GET /appraisals/{id}/pipeline. Active run lookup
 * comes from GET /workflow-runs?status=in_progress (client-side filter).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, UserPlus, UserCheck, Play, ArrowLeft, ExternalLink, ShieldCheck } from 'lucide-react';

import { api, getPipeline, listRuns, startWorkflowRun, useStatusLabels } from './pipelineApi';
import StatusPipelineTabs from './components/StatusPipelineTabs';
import StatusRail          from './components/StatusRail';
import TaskTreeMiddle      from './components/TaskTreeMiddle';
import PipelineGateBanner  from './components/PipelineGateBanner';
import RunnerAuditModal    from './components/RunnerAuditModal';
import WorkflowRunnerPage  from './WorkflowRunnerPage';

export default function WorkflowConsolePage() {
  const { appraisalId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [pipeline, setPipeline]     = useState(null);
  const [activeKey, setActiveKey]   = useState('');
  const [activeActionId, setActiveActionId] = useState(null);
  const [runsForAppraisal, setRuns] = useState([]);
  const [activeRunId, setActiveRunId] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [starting, setStarting]     = useState(false);
  const [err, setErr]               = useState('');
  // iter303aj-fix7 · Runner audit modal (T2/T3 buttons).
  const [auditModal, setAuditModal] = useState(null); // null | {scope:'status', statusKey} | {scope:'runner'}

  const reloadPipeline = useCallback(async () => {
    try {
      const p = await getPipeline(appraisalId);
      setPipeline(p);
      if (!activeKey) {
        // iter303ai-fix7 — honour a `?status=` query hint from the runner's
        // finalize handoff when the target status is unlocked. Otherwise
        // fall back to the pipeline's current status.
        const hinted = searchParams.get('status');
        const hintedRow = hinted ? (p.ladder || []).find(r => r.status_key === hinted) : null;
        const useHint = hintedRow && hintedRow.state !== 'locked';
        setActiveKey(useHint ? hinted : (p.current_status_key || 'client_data'));
        // Consume the query so a manual tab switch afterwards doesn't get
        // overridden on the next reload.
        if (hinted) {
          const next = new URLSearchParams(searchParams);
          next.delete('status');
          setSearchParams(next, { replace: true });
        }
      }
      return p;
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Load failed');
      return null;
    }
  }, [appraisalId, activeKey, searchParams, setSearchParams]);

  const reloadRuns = useCallback(async () => {
    try {
      const rs = await listRuns({ status: 'in_progress' });
      const mine = (rs || []).filter(r => r.appraisal_id === appraisalId);
      setRuns(mine);
    } catch { /* silent */ }
  }, [appraisalId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setErr('');
      const p = await reloadPipeline();
      await reloadRuns();
      if (alive) setLoading(false);
      if (!p) return;
    })();
    return () => { alive = false; };
  }, [reloadPipeline, reloadRuns]);

  // Iter303ai · Option C — Remember this appraisal so the sidebar link
  // "Workflow Runner" auto-resumes the operator on it next time.
  useEffect(() => {
    if (appraisalId) {
      try { localStorage.setItem('overwatch_last_appraisal_id', appraisalId); } catch { /* quota — ignore */ }
    }
  }, [appraisalId]);

  // Iter303ai-fix3 · Pre-highlight the current action on load / status switch
  useEffect(() => {
    if (!pipeline) return;
    if (!activeKey) return;
    // Only sync when there's a real current action for the active status
    const row = pipeline.ladder?.find(r => r.status_key === activeKey);
    if (row && row.current_action_id != null) {
      setActiveActionId(row.current_action_id);
    }
  }, [pipeline, activeKey]);

  const activeRow = useMemo(() =>
    pipeline?.ladder?.find(r => r.status_key === activeKey) || null,
    [pipeline, activeKey],
  );

  // Pick the in-progress run matching the active status (if any)
  useEffect(() => {
    // Pre-UAT v10.10 fix (Feb 2026 · UAT A999565) — prefer an `in_progress`
    // run over any `task_complete`/`abandoned` siblings. Without this, the
    // reload race after `finalize()` in a multi-task status re-picks a
    // stale `task_complete` run and clobbers the newly-set activeRunId,
    // trapping the operator on the Task-Complete screen in a loop.
    const rn = runsForAppraisal.find(r => r.status_key === activeKey && r.status === 'in_progress')
            || runsForAppraisal.find(r => r.status_key === activeKey);
    setActiveRunId(rn?.id || null);
  }, [activeKey, runsForAppraisal]);

  const handleTabPick = (statusKey) => {
    setActiveKey(statusKey);
    setActiveActionId(null);
  };

  const handleStartRun = async (entryChoice) => {
    setStarting(true); setErr('');
    try {
      const run = await startWorkflowRun({
        statusKey: activeKey,
        appraisalId: appraisalId,
        entryChoice,   // only meaningful for client_data
      });
      setActiveRunId(run.id);
      await reloadRuns();
      await reloadPipeline();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Start failed');
    } finally { setStarting(false); }
  };

  // Iter303ai-fix3 · Click-to-navigate in the task tree.
  // ────────────────────────────────────────────────────
  // Clicking an action row (in the currently-running task) PATCHes the
  // workflow_run's current_action_id, then bumps `runnerReloadKey` so
  // the embedded WorkflowRunnerPage unmounts + remounts and re-fetches
  // the run — landing directly on the clicked action.
  const [runnerReloadKey, setRunnerReloadKey] = useState(0);
  const handlePickAction = async (task, action) => {
    setActiveActionId(action.id);
    if (!activeRunId) return;   // no run yet — clicking merely highlights
    try {
      await api.patch(`/workflow-runs/${activeRunId}`, {
        current_action_id: action.id,
      });
      setRunnerReloadKey(k => k + 1);
    } catch { /* silent — highlight already updated */ }
  };

  // Iter303ai-fix4 · Cross-task switch — clicking a task heading in a
  // DIFFERENT task from the currently-running one prompts to abandon
  // the current run and start the new task (existing_client vs new_client).
  const handleSwitchTask = async (task) => {
    if (!activeRunId) return;   // no run — no switch needed
    if (!window.confirm(`Switch to "${task.label}"? The current task's progress will be discarded.`)) return;
    try {
      await api.delete(`/workflow-runs/${activeRunId}`);
    } catch { /* silent */ }
    let entryChoice;
    const lbl = (task.label || '').toLowerCase();
    if (lbl.includes('new client'))      entryChoice = 'new_client';
    if (lbl.includes('existing client')) entryChoice = 'existing_client';
    setActiveRunId(null);
    await reloadRuns();
    await reloadPipeline();
    if (activeKey === 'client_data' && entryChoice) {
      handleStartRun(entryChoice);
    }
  };

  // Track the current run's task_id so TaskTreeMiddle can grey out
  // actions in other tasks (they're not addressable via this run).
  const currentTaskId = React.useMemo(() => {
    const rn = runsForAppraisal.find(r => r.status_key === activeKey);
    return rn?.task_id ?? null;
  }, [runsForAppraisal, activeKey]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-50">
        <Loader2 size={20} className="animate-spin text-slate-400" />
      </div>
    );
  }
  if (!pipeline) {
    // The remembered appraisal is gone (deleted / permissions). Clear the
    // localStorage marker so the next sidebar click lands on the picker
    // instead of looping back here.
    try { localStorage.removeItem('overwatch_last_appraisal_id'); } catch { /* noop */ }
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-50 p-8" data-testid="wf-console-error">
        <div className="max-w-md text-center">
          <div className="text-rose-700 bg-rose-50 border border-rose-200 rounded p-4 mb-4">
            {err || 'This appraisal is no longer available.'}
          </div>
          <button
            onClick={() => navigate('/workflow/start?pick=1')}
            data-testid="wf-console-pick-another"
            className="px-4 py-2 bg-sky-600 text-white text-sm font-semibold rounded hover:bg-sky-700"
          >
            Pick another appraisal →
          </button>
        </div>
      </div>
    );
  }

  const header = pipeline.header || {};

  return (
    <div className="h-screen flex flex-col bg-slate-50" data-testid="wf-console">
      {/* ── Top: pipeline tabs + appraisal header ─────────────── */}
      <StatusPipelineTabs
        ladder={pipeline.ladder || []}
        activeKey={activeKey}
        onPick={handleTabPick}
      />
      <div className="border-b border-slate-200 bg-white px-4 py-2 flex items-center gap-3">
        <button onClick={() => navigate('/workflow/start?pick=1')}
          data-testid="wf-back-picker"
          className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1">
          <ArrowLeft size={14} /> Switch appraisal
        </button>
        <span className="text-slate-300">·</span>
        {/* iter303aj-fix8 · A## / C## / P## number pills + client name + property address */}
        <div className="text-xs text-slate-500" data-testid="wf-header-appraisal-no">
          <span className="uppercase tracking-widest text-[10px] text-slate-400 mr-1">A</span>
          <span className="font-semibold text-slate-800">{header.appraisal_no || '—'}</span>
        </div>
        <span className="text-slate-300">·</span>
        <div className="text-xs text-slate-500" data-testid="wf-header-client-no">
          <span className="uppercase tracking-widest text-[10px] text-slate-400 mr-1">C</span>
          <span className="font-semibold text-slate-800">{header.client_no || '—'}</span>
        </div>
        <span className="text-slate-300">·</span>
        <div className="text-xs text-slate-500" data-testid="wf-header-property-no">
          <span className="uppercase tracking-widest text-[10px] text-slate-400 mr-1">P</span>
          <span className="font-semibold text-slate-800">{header.property_no || '—'}</span>
        </div>
        <span className="text-slate-300">·</span>
        <div className="text-xs text-slate-500" data-testid="wf-header-client-name">
          <span className="uppercase tracking-widest text-[10px] text-slate-400 mr-1">Client</span>
          <span className="font-semibold text-slate-800">{header.client_name || '—'}</span>
        </div>
        <span className="text-slate-300">·</span>
        <div className="text-xs text-slate-500 truncate max-w-[280px]" data-testid="wf-header-property-address" title={header.property_address || ''}>
          <span className="uppercase tracking-widest text-[10px] text-slate-400 mr-1">Address</span>
          <span className="font-semibold text-slate-800">{header.property_address || '—'}</span>
        </div>
        {header.legacy_status === 'legacy_pending_review' && (
          <span className="text-[10px] uppercase tracking-widest text-slate-400 bg-slate-100 px-2 py-0.5 rounded" data-testid="wf-legacy-status">
            Legacy · Pending Review
          </span>
        )}
        {/* iter303aj-fix7 · Runner audit buttons — status-scoped + runner-wide */}
        <div className="ml-auto">
          <div className="inline-flex rounded border border-slate-200 bg-white overflow-hidden text-[11px] font-semibold">
            <button
              onClick={() => setAuditModal({ scope: 'status', statusKey: activeKey })}
              disabled={!activeKey}
              data-testid="wf-audit-status-btn"
              title="Audit the currently-viewed status only"
              className="flex items-center gap-1.5 px-3 py-1.5 text-slate-600 hover:text-sky-700 hover:bg-sky-50 border-r border-slate-200 disabled:opacity-40">
              <ShieldCheck size={12} /> Audit Status
            </button>
            <button
              onClick={() => setAuditModal({ scope: 'runner' })}
              data-testid="wf-audit-runner-btn"
              title="Audit all 11 statuses"
              className="flex items-center gap-1.5 px-3 py-1.5 text-slate-600 hover:text-sky-700 hover:bg-sky-50">
              <ShieldCheck size={12} /> Audit Runner
            </button>
          </div>
        </div>
      </div>

      {/* Audit modal */}
      {auditModal && (
        <RunnerAuditModal
          scope={auditModal.scope}
          statusKey={auditModal.statusKey}
          onClose={() => setAuditModal(null)}
        />
      )}

      {/* ── 3-column body ─────────────────────────────────────── */}
      <div className="flex-1 flex overflow-hidden">
        <StatusRail
          ladder={pipeline.ladder || []}
          activeKey={activeKey}
          onPick={handleTabPick}
        />
        <TaskTreeMiddle
          statusRow={activeRow}
          currentTaskId={currentTaskId}
          onPickAction={handlePickAction}
          onSwitchTask={handleSwitchTask}
          activeActionId={activeActionId}
        />

        {/* Right pane */}
        <main className="flex-1 overflow-auto bg-white">
          {activeRow?.state === 'locked' && (
            <>
              <PipelineGateBanner ladder={pipeline.ladder || []} statusKey={activeKey} />
              <div className="p-6 text-sm text-slate-500 italic">
                This status is locked. Use the task list on the left to preview what work is coming.
              </div>
            </>
          )}

          {activeRow?.state !== 'locked' && !activeRunId && (
            <RightPaneEntryChoice
              statusKey={activeKey}
              starting={starting}
              onStart={handleStartRun}
              err={err}
              headerClientName={pipeline?.header?.client_name}
              hasExistingClient={!!pipeline?.header?.client_id}
              stageComplete={activeRow?.state === 'complete'}
              nextKey={pipeline?.current_status_key && pipeline.current_status_key !== activeKey ? pipeline.current_status_key : ''}
              onGoNext={(k) => setActiveKey(k)}
            />
          )}

          {activeRow?.state !== 'locked' && activeRunId && (
            <div data-testid="wf-runner-embedded">
              {/* Reuse the existing runner page — it drives the /workflow/:runId
                  route already; we render it inline via a synthetic URL.
                  `key` bumps whenever handlePickAction jumps the pointer
                  so the runner unmounts + remounts and re-fetches state. */}
              <WorkflowRunnerPage
                key={`${activeRunId}-${runnerReloadKey}`}
                embeddedRunId={activeRunId}
                onRunFinished={async (result) => {
                  // iter303ai-fix10 · If more sibling tasks remain in this
                  // status, finalize() hands us back a `next_run_id` — swap
                  // in place instead of dropping back to the task picker.
                  //
                  // v10.16 (Feb 2026 · UAT A999577 mid-check freeze) · Also
                  // bump `runnerReloadKey` so the embedded runner ALWAYS
                  // unmounts+remounts even if React batches `setActiveRunId`
                  // in a way that skips the render. Without this bump, the
                  // stale "Finalizing…" child stayed mounted and the user
                  // had to Ctrl+Shift+R to force a reload.
                  //
                  // v10.22 (Feb 2026 · UAT A999617 freeze diagnosis) —
                  // emit timing logs so the next "Finalizing…" freeze
                  // tells us exactly which reload stalls. All 5 checkpoints
                  // are cheap console.log calls; look for them in F12
                  // Console. If total > 5s the operator sees a spinner.
                  // eslint-disable-next-line no-console
                  console.log('[console-onRunFinished] start', { next_run_id: result?.next_run_id, next_status_key: result?.next_status_key });
                  const _t0 = performance.now();
                  if (result && result.next_run_id) {
                    // A stage-finishing PATCH hands back only the new run — switch tabs to that run's stage.
                    try {
                      const { data: nr } = await api.get(`/workflow-runs/${result.next_run_id}`);
                      if (nr?.status_key) setActiveKey(nr.status_key);
                    } catch { /* stay on current tab */ }
                    setActiveRunId(result.next_run_id);
                    setActiveActionId(null);
                    setRunnerReloadKey(k => k + 1);
                    // eslint-disable-next-line no-console
                    console.log('[console-onRunFinished] state-batched, awaiting reloadRuns…', { elapsed_ms: Math.round(performance.now() - _t0) });
                    await reloadRuns();
                    // eslint-disable-next-line no-console
                    console.log('[console-onRunFinished] reloadRuns done, awaiting reloadPipeline…', { elapsed_ms: Math.round(performance.now() - _t0) });
                    await reloadPipeline();
                    // eslint-disable-next-line no-console
                    console.log('[console-onRunFinished] mid-status hop DONE ✅', { elapsed_ms: Math.round(performance.now() - _t0) });
                    return;
                  }
                  setActiveRunId(null);
                  setActiveActionId(null);
                  setRunnerReloadKey(k => k + 1);
                  // eslint-disable-next-line no-console
                  console.log('[console-onRunFinished] status-jump state-batched, awaiting reloadRuns…', { elapsed_ms: Math.round(performance.now() - _t0) });
                  await reloadRuns();
                  // eslint-disable-next-line no-console
                  console.log('[console-onRunFinished] reloadRuns done, awaiting reloadPipeline…', { elapsed_ms: Math.round(performance.now() - _t0) });
                  const p = await reloadPipeline();
                  // eslint-disable-next-line no-console
                  console.log('[console-onRunFinished] status-jump DONE ✅', { elapsed_ms: Math.round(performance.now() - _t0) });
                  // iter303ai-fix7 — after finalize, jump to the newly-unlocked
                  // next status so the operator can start it right away.
                  if (result && result.next_status_key && p) {
                    const nextRow = (p.ladder || []).find(r => r.status_key === result.next_status_key);
                    if (nextRow && nextRow.state !== 'locked') {
                      setActiveKey(result.next_status_key);
                    }
                  }
                }}
              />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Right-pane "no active run" state
// ─────────────────────────────────────────────────────────────
function RightPaneEntryChoice({ statusKey, starting, onStart, err, headerClientName, hasExistingClient, stageComplete, nextKey, onGoNext }) {
  const STATUS_LABELS = useStatusLabels();
  if (stageComplete && statusKey !== 'client_data') {
    return (
      <div className="p-8" data-testid={`wf-stage-complete-${statusKey}`}>
        <h1 className="text-3xl font-bold text-slate-900">{STATUS_LABELS[statusKey]} is complete</h1>
        <p className="text-slate-500 mt-2 mb-8">Every task in this stage is done.</p>
        {nextKey && (
          <button onClick={() => onGoNext(nextKey)} data-testid="wf-go-next-status"
            className="px-6 py-3 bg-sky-600 text-white font-semibold rounded hover:bg-sky-700 flex items-center gap-2">
            <Play size={16} /> Go to {STATUS_LABELS[nextKey] || nextKey}
          </button>
        )}
      </div>
    );
  }
  if (statusKey === 'client_data') {
    return (
      <div className="p-8" data-testid="wf-entry-choice-client-data">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2">
          Overwatch Runner · Client Data
        </div>
        {hasExistingClient ? (
          <>
            <h1 className="text-3xl font-bold text-slate-900">
              Continue with the existing client on file
            </h1>
            <p className="text-slate-500 mt-2 mb-8">
              This appraisal is already linked to <span className="font-semibold text-slate-800">{headerClientName || 'a client on record'}</span>.
              Confirm their details below — or switch to a fresh New Client entry if you need to create a different record.
            </p>
            {err && <div className="mb-4 text-sm text-rose-600" data-testid="wf-entry-error">{err}</div>}
            <div className="max-w-3xl">
              <button
                onClick={() => onStart('existing_client')}
                disabled={starting}
                data-testid="wf-start-existing-client"
                className="w-full text-left border-2 border-emerald-200 hover:border-emerald-400 hover:shadow-lg bg-emerald-50/40 rounded-2xl p-6 transition disabled:opacity-40"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
                    <UserCheck size={24} className="text-emerald-700" />
                  </div>
                  <div className="flex-1">
                    <div className="text-xl font-bold text-slate-900">Continue Existing Client</div>
                    <div className="text-sm text-slate-500">{headerClientName || 'Client on record'}</div>
                  </div>
                  <div className="text-emerald-700 text-sm font-semibold flex items-center gap-1">
                    Start <ExternalLink size={12} />
                  </div>
                </div>
              </button>

              <div className="mt-6 pt-6 border-t border-slate-200">
                <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-2">
                  Need a different client instead?
                </div>
                <button
                  onClick={() => {
                    if (window.confirm('This will start a NEW-CLIENT workflow — a fresh client record will be created for this appraisal. Continue?')) {
                      onStart('new_client');
                    }
                  }}
                  disabled={starting}
                  data-testid="wf-start-new-client"
                  className="text-sm text-slate-500 hover:text-sky-700 underline decoration-dotted flex items-center gap-1"
                >
                  <UserPlus size={13} /> Start over as New Client (creates a new record)
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <h1 className="text-3xl font-bold text-slate-900">Are you an existing client or new to us?</h1>
            <p className="text-slate-500 mt-2 mb-8">Pick one to begin the guided appraisal workflow.</p>
            {err && <div className="mb-4 text-sm text-rose-600" data-testid="wf-entry-error">{err}</div>}
            <div className="grid grid-cols-2 gap-6 max-w-3xl">
              <EntryCard
                title="New Client"
                desc="First-time caller — capture full details."
                Icon={UserPlus}
                tint="sky"
                onClick={() => onStart('new_client')}
                starting={starting}
                testId="wf-start-new-client"
              />
              <EntryCard
                title="Existing Client"
                desc="Look up records and confirm details."
                Icon={UserCheck}
                tint="emerald"
                onClick={() => onStart('existing_client')}
                starting={starting}
                testId="wf-start-existing-client"
              />
            </div>
          </>
        )}
      </div>
    );
  }
  return (
    <div className="p-8" data-testid={`wf-entry-choice-${statusKey}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2">
        Overwatch Runner · {STATUS_LABELS[statusKey]}
      </div>
      <h1 className="text-3xl font-bold text-slate-900">Ready to work on this status?</h1>
      <p className="text-slate-500 mt-2 mb-8">Click below to open the guided task list for this appraisal.</p>
      {err && <div className="mb-4 text-sm text-rose-600" data-testid="wf-entry-error">{err}</div>}
      <button
        onClick={() => onStart(undefined)}
        disabled={starting}
        data-testid="wf-start-status"
        className="px-6 py-3 bg-sky-600 text-white font-semibold rounded hover:bg-sky-700 disabled:opacity-40 flex items-center gap-2"
      >
        {starting ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
        Start {STATUS_LABELS[statusKey]}
      </button>
    </div>
  );
}

function EntryCard({ title, desc, Icon, tint, onClick, starting, testId }) {
  return (
    <button
      onClick={onClick}
      disabled={starting}
      data-testid={testId}
      className={`
        group border-2 rounded-2xl p-8 text-left transition
        border-${tint}-200 hover:border-${tint}-400 hover:shadow-lg
        disabled:opacity-40 disabled:cursor-not-allowed
      `}
    >
      <div className={`w-14 h-14 rounded-full bg-${tint}-100 flex items-center justify-center mb-4 group-hover:bg-${tint}-200`}>
        <Icon size={26} className={`text-${tint}-700`} />
      </div>
      <div className="text-xl font-bold text-slate-900">{title}</div>
      <div className="text-sm text-slate-500 mt-1">{desc}</div>
      <div className={`mt-4 text-xs uppercase tracking-widest text-${tint}-700 font-semibold flex items-center gap-1`}>
        Start <ExternalLink size={11} />
      </div>
    </button>
  );
}
